import { useCallback, useEffect, useRef } from 'react';
import { useStudioStore } from './stores/useStudioStore.js';
import { Home } from './pages/Home.js';
import { Studio } from './pages/Studio.js';
import { Projects } from './pages/Projects.js';
import { session } from './session.js';
import { projectStorage } from './audio/ProjectStorageEngine.js';
import { Icon } from './components/Icon.js';
import type { InstrumentalRef } from './types.js';

export default function App() {
  const screen = useStudioStore((s) => s.screen);
  const project = useStudioStore((s) => s.project);
  const toast = useStudioStore((s) => s.toast);
  const setToast = useStudioStore((s) => s.setToast);
  const setScreen = useStudioStore((s) => s.setScreen);
  const setProject = useStudioStore((s) => s.setProject);
  const setInstallPrompt = useStudioStore((s) => s.setInstallPrompt);
  const previousScreen = useRef<'home' | 'studio'>('home');
  const toastTimer = useRef<number | null>(null);

  useEffect(() => {
    const before = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event);
    };
    const installed = () => setInstallPrompt(null);
    window.addEventListener('beforeinstallprompt', before as EventListener);
    window.addEventListener('appinstalled', installed);
    return () => {
      window.removeEventListener('beforeinstallprompt', before as EventListener);
      window.removeEventListener('appinstalled', installed);
    };
  }, [setInstallPrompt]);

  useEffect(() => {
    if (toastTimer.current != null) clearTimeout(toastTimer.current);
    if (toast) toastTimer.current = window.setTimeout(() => setToast(null), 5200);
    return () => { if (toastTimer.current != null) clearTimeout(toastTimer.current); };
  }, [toast, setToast]);

  const openProjects = useCallback(() => {
    const current = useStudioStore.getState().screen;
    if (current === 'home' || current === 'studio') previousScreen.current = current;
    if (current === 'studio') {
      session.playback.stop();
      session.recorder.release();
      session.audio.setMonitor(false);
      useStudioStore.getState().setMic({ micReady: false, micStatus: 'idle', micDevices: [], selectedMicId: '', micLevel: { peak: 0, rms: 0 } });
    }
    setScreen('projects');
  }, [setScreen]);

  const leaveStudio = useCallback(() => {
    session.playback.stop();
    session.recorder.release();
    session.audio.setMonitor(false);
    useStudioStore.getState().setMic({ micReady: false, micStatus: 'idle', micDevices: [], selectedMicId: '', micLevel: { peak: 0, rms: 0 } });
    setScreen('home');
  }, [setScreen]);

  const install = async () => {
    const prompt = useStudioStore.getState().installPrompt;
    if (!prompt) return;
    try {
      await prompt.prompt();
      const result = await prompt.userChoice;
      if (result?.outcome === 'accepted') setToast({ tone: 'success', title: 'VOCAL STUDIO instalado', description: 'O app está disponível na sua tela inicial.' });
    } catch {
      setToast({ tone: 'info', title: 'Instalação pelo navegador', description: 'Use o menu do navegador e escolha “Adicionar à tela inicial”.' });
    } finally {
      setInstallPrompt(null);
    }
  };

  const importInstrumental = async (file: File, addToCurrent = false) => {
    if (!file) throw new Error('Escolha um arquivo de áudio.');
    if (file.size > 500 * 1024 * 1024) throw new Error('Este áudio tem mais de 500 MB. Tente um arquivo menor.');
    let buffer: AudioBuffer;
    try {
      const ctx = await session.audio.ensure();
      const bytes = await file.arrayBuffer();
      buffer = await ctx.decodeAudioData(bytes.slice(0));
    } catch (err) {
      const detail = err instanceof Error ? err.message : '';
      throw new Error(detail.includes('AudioContext') ? detail : 'Este formato não pôde ser lido neste navegador. Tente WAV, MP3 ou M4A.');
    }
    if (!buffer.length || !buffer.duration) throw new Error('O arquivo não contém áudio utilizável.');

    const now = Date.now();
    const ref: InstrumentalRef = {
      id: `inst_${now.toString(36)}`,
      name: file.name.replace(/\.[^.]+$/, '').slice(0, 100) || 'Instrumental autorizado',
      bytes: file.size,
      duration: buffer.duration,
      sampleRate: buffer.sampleRate,
      rightsAccepted: true,
      createdAt: now,
    };
    let doc = useStudioStore.getState().project;
    const isAddingToExisting = addToCurrent && !!doc;
    if (!addToCurrent || !doc) {
      useStudioStore.getState().createProject({ title: ref.name, sourceType: 'file' });
      doc = useStudioStore.getState().project;
    }
    if (!doc) throw new Error('Não foi possível criar o projeto.');
    // Preserve o modo YouTube como referência quando o instrumental é adicionado
    // após a gravação; no projeto de áudio, o tipo continua sendo "file".
    const next = {
      ...doc,
      title: doc.title === 'Novo projeto' || doc.title === 'Gravação rápida' ? ref.name : doc.title,
      sourceType: addToCurrent && doc.sourceType === 'youtube' ? 'youtube' as const : doc.sourceType === 'none' ? 'file' as const : doc.sourceType,
      instrumental: ref,
      instrumentalBuffer: buffer,
      updatedAt: Date.now(),
    };
    if (isAddingToExisting) useStudioStore.getState().updateProject(next);
    else setProject(next, true);
    session.instrumental = {
      buffer,
      left: Float32Array.from(buffer.getChannelData(0)),
      right: buffer.numberOfChannels > 1 ? Float32Array.from(buffer.getChannelData(1)) : Float32Array.from(buffer.getChannelData(0)),
      sampleRate: buffer.sampleRate,
      duration: buffer.duration,
      name: ref.name,
    };
    session.audio.setInstrumentalBuffer(buffer);
    session.playback.setInstrumentalBuffer(buffer);
    session.adapter = next.sourceType === 'youtube' ? session.adapter : null;
    if (next.sourceType !== 'youtube') session.playback.setAdapter(null);
    session.audio.setInstrumentalVolume(next.mix.instrumentalVolume);
    setScreen('studio');
    setToast({ tone: 'success', title: 'Instrumental carregado', description: `${ref.name} · ${Math.round(buffer.duration)} s · ${Math.round(buffer.sampleRate / 1000)} kHz · processado localmente.` });
  };

  const openProject = async (id: string) => {
    const doc = await projectStorage.loadProject(id);
    if (!doc) {
      setToast({ tone: 'error', title: 'Projeto indisponível', description: projectStorage.lastError ?? 'Não foi possível recuperar o projeto local.' });
      return;
    }
    setProject(doc, false);
    setScreen('studio');
    if (doc.instrumentalBuffer) {
      const b = doc.instrumentalBuffer;
      session.instrumental = {
        buffer: b,
        left: Float32Array.from(b.getChannelData(0)),
        right: b.numberOfChannels > 1 ? Float32Array.from(b.getChannelData(1)) : Float32Array.from(b.getChannelData(0)),
        sampleRate: b.sampleRate,
        duration: b.duration,
        name: doc.instrumental?.name ?? 'Instrumental autorizado',
      };
    }
  };

  const backFromProjects = () => {
    const target = previousScreen.current === 'studio' && project ? 'studio' : 'home';
    setScreen(target);
  };

  return (
    <>
      {screen === 'home' && <Home onImportInstrumental={(file) => importInstrumental(file, false)} onOpenProjects={openProjects} onInstall={install} />}
      {screen === 'projects' && <Projects onOpen={openProject} onBack={backFromProjects} />}
      {screen === 'studio' && <Studio onHome={leaveStudio} onOpenProjects={openProjects} onImportInstrumental={importInstrumental} />}
      {toast && <div className={`toast toast-${toast.tone}`} role="status" aria-live="polite"><span className="toast-icon"><Icon name={toast.tone==='error'?'alert':toast.tone==='warning'?'info':toast.tone==='success'?'checkCircle':'info'} size={16}/></span><div><b>{toast.title}</b>{toast.description&&<span>{toast.description}</span>}</div><button onClick={()=>setToast(null)} aria-label="Fechar notificação"><Icon name="close" size={15}/></button></div>}
    </>
  );
}
